<template>
  <div class="text-left w-100">
    <div v-if="loading" class="mt-8">
      <v-progress-circular indeterminate aria-label="Chargement des résultats"></v-progress-circular>
    </div>

    <div v-else-if="error" class="mt-8">
      <v-alert type="error" text>{{ error }}</v-alert>
    </div>

    <template v-else-if="restitution">
      <h1
        class="my-2 text-h6 font-weight-black"
        :style="$vuetify.breakpoint.mdAndUp ? 'font-size: 2rem !important;' : ''"
      >
        {{ restitution.actionTitle }}
      </h1>

      <!-- Le contexte est dit en entier : deux activités bâties sur la même recommandation se
           ressemblent, et seule cette ligne permet de savoir laquelle on regarde. -->
      <p class="text-body-2 grey--text text--darken-2 mb-1">
        {{ restitution.canteenName }}
        <span v-if="restitution.experienceTitle">· d'après « {{ restitution.experienceTitle }} »</span>
      </p>
      <p v-if="surveyMention" class="text-body-2 grey--text text--darken-1">{{ surveyMention }}</p>

      <v-card outlined class="dsfr pa-4 my-4">
        <p class="text-h6 mb-1">{{ respondentSentence }}</p>
        <p v-if="restitution.breakdownWithheld" class="text-body-2 mb-0 grey--text text--darken-2">
          Le détail par question s'affiche à partir de {{ restitution.minRespondentsForBreakdown }} répondants, pour
          qu'aucune réponse ne puisse être rattachée à une personne.
        </p>
      </v-card>

      <div v-if="restitution.questions.length === 0">
        <p class="text-body-1 grey--text text--darken-1">Cette activité ne porte aucun questionnaire.</p>
      </div>

      <v-card v-for="question in restitution.questions" :key="question.id" outlined class="dsfr pa-4 mb-4">
        <h2 class="text-subtitle-1 font-weight-bold mb-3">{{ question.label }}</h2>

        <!-- Répartition : chaque ligne porte son effectif en toutes lettres. La barre ne fait que
             doubler un texte déjà lisible, elle ne porte aucune information à elle seule (RGAA). -->
        <table v-if="question.kind === 'choice'" class="distribution">
          <caption class="d-sr-only">Répartition des réponses à « {{ question.label }} »</caption>
          <tbody>
            <tr v-for="row in question.distribution" :key="row.label">
              <th scope="row" class="text-left font-weight-regular">{{ row.label }}</th>
              <td class="text-right text-no-wrap pl-4">{{ row.count }} ({{ row.share }} %)</td>
              <td class="bar-cell pl-4">
                <span class="bar" :style="{ width: row.share + '%' }" aria-hidden="true"></span>
              </td>
            </tr>
          </tbody>
        </table>

        <p v-else-if="question.kind === 'numeric'" class="mb-0">
          <span v-if="question.mean === null">Aucune réponse chiffrée.</span>
          <span v-else>
            Moyenne {{ question.mean }} — de {{ question.min }} à {{ question.max }}, sur
            {{ question.answerCount }} réponses.
          </span>
        </p>

        <!-- Les réponses libres sont dénombrées, jamais citées : un verbatim désigne souvent son
             auteur dans un établissement de petite taille. -->
        <p v-else-if="question.kind === 'free_text'" class="mb-0 grey--text text--darken-2">
          {{ question.answerCount }} réponse{{ question.answerCount > 1 ? "s" : "" }} en texte libre. Les réponses
          rédigées ne sont pas restituées ici : elles permettraient d'identifier leur auteur.
        </p>

        <p v-else class="mb-0 grey--text text--darken-2">
          Détail masqué tant que l'activité n'a pas réuni {{ restitution.minRespondentsForBreakdown }} répondants.
        </p>
      </v-card>
    </template>
  </div>
</template>

<script>
/**
 * Restitution des réponses d'une activité.
 *
 * Remplace la redirection vers le tableau de bord d'administration de `djf_surveys`, qui
 * agrégeait les réponses de tous les établissements et de toutes les campagnes — d'où un écran
 * réservé aux auteurs du référentiel, et un 403 opposé au gestionnaire. Ici la restitution est
 * cadrée sur l'activité et sur le périmètre de l'appelant, et rien d'individuel n'y paraît.
 */
export default {
  name: "ActionResults",
  data() {
    return {
      restitution: null,
      loading: true,
      error: null,
    }
  },
  computed: {
    respondentSentence() {
      const count = this.restitution.respondentCount
      if (count === 0) return "Aucune réponse pour le moment"
      return `${count} réponse${count > 1 ? "s" : ""} recueillie${count > 1 ? "s" : ""}`
    },
    surveyMention() {
      const names = this.restitution.surveyNames || []
      if (names.length === 0) return ""
      return `Questionnaire${names.length > 1 ? "s" : ""} : ${names.join(", ")}`
    },
  },
  mounted() {
    this.$store
      .dispatch("fetchActionResponses", { actionId: this.$route.params.id })
      .then((response) => {
        this.restitution = response
      })
      .catch(() => {
        this.error = "Ces résultats ne sont pas accessibles."
      })
      .finally(() => {
        this.loading = false
      })
  },
}
</script>

<style scoped>
.distribution {
  width: 100%;
  border-collapse: collapse;
}

.distribution th,
.distribution td {
  padding: 0.25rem 0;
  vertical-align: middle;
}

.bar-cell {
  width: 50%;
}

.bar {
  display: block;
  height: 0.5rem;
  min-width: 2px;
  border-radius: 2px;
  background-color: #000091;
}
</style>
